(() => {
  let adding = false;
  const root = () => window.Shopify?.routes?.root || '/';
  const galleryBindings = new WeakSet();
  const stickyBindings = new WeakSet();

  function setup(scope = document) {
    scope.querySelectorAll('[data-oc-gallery]').forEach((gallery) => {
      if (galleryBindings.has(gallery)) return;
      galleryBindings.add(gallery);
      const thumbs = [...gallery.querySelectorAll('[data-oc-thumb]')];
      const dots = [...gallery.querySelectorAll('[data-oc-dot]')];
      const image = gallery.querySelector('.oc-hero__main-image');
      const main = gallery.querySelector('.oc-hero__main');
      let active = 0;
      function show(index) {
        if (!image || !thumbs.length) return;
        active = (index + thumbs.length) % thumbs.length;
        const thumb = thumbs[active];
        image.src = thumb.dataset.ocSrc;
        image.srcset = thumb.dataset.ocSrcset;
        image.alt = thumb.dataset.ocAlt;
        thumbs.forEach((button, i) => {
          button.classList.toggle('oc-hero__thumb--active', i === active);
          button.setAttribute('aria-pressed', String(i === active));
        });
        dots.forEach((dot, i) => dot.classList.toggle('oc-hero__dot--active', i === active));
      }
      thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => show(index)));
      gallery.querySelectorAll('[data-oc-tap]').forEach((button) => {
        button.addEventListener('click', () => show(active + (button.dataset.ocTap === 'prev' ? -1 : 1)));
      });
      let start;
      main?.addEventListener('touchstart', (event) => {
        start = event.touches.length === 1 ? [event.touches[0].clientX, event.touches[0].clientY] : null;
      }, { passive: true });
      main?.addEventListener('touchend', (event) => {
        if (!start || !event.changedTouches.length) return;
        const dx = event.changedTouches[0].clientX - start[0];
        const dy = event.changedTouches[0].clientY - start[1];
        start = null;
        if (Math.abs(dx) >= 40 && Math.abs(dx) > Math.abs(dy) * 1.4) show(active + (dx < 0 ? 1 : -1));
      });
      main?.addEventListener('touchcancel', () => { start = null; }, { passive: true });
    });

    scope.querySelectorAll('oc-sticky-cart').forEach((sticky) => {
      if (stickyBindings.has(sticky)) return;
      stickyBindings.add(sticky);
      const hero = document.querySelector('.oc-hero__cta');
      if (!hero) return;
      function clearance() {
        document.body.style.setProperty('--oc-cart-clearance', `${Math.ceil(sticky.getBoundingClientRect().height) + 24}px`);
      }
      function update() {
        const visible = hero.getBoundingClientRect().bottom <= 0;
        sticky.dataset.stuck = String(visible);
        sticky.toggleAttribute('inert', !visible);
        clearance();
      }
      update();
      if ('IntersectionObserver' in window) new IntersectionObserver(update).observe(hero);
      if ('ResizeObserver' in window) new ResizeObserver(clearance).observe(sticky);
      window.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update, { passive: true });
      window.addEventListener('pageshow', update);
    });
  }

  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-oc-add-form]')) return;
    event.preventDefault();
    if (adding) return;
    const button = form.querySelector('[data-oc-add-button]');
    if (!button || button.dataset.ocAvailable !== 'true') return;
    adding = true;
    const label = button.querySelector('.oc-hero__cta-label');
    const original = label?.textContent;
    const status = form.parentElement.querySelector('[data-oc-cart-status]');
    const buttons = [...document.querySelectorAll('[data-oc-add-button]')];
    buttons.forEach((item) => { item.disabled = true; });
    form.setAttribute('aria-busy', 'true');
    if (label) label.textContent = button.dataset.ocAdding;
    if (status) {
      status.removeAttribute('data-error');
      status.textContent = button.dataset.ocAdding;
    }
    let confirmed = false;
    try {
      const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map((item) => item.dataset.sectionId))];
      const body = new FormData(form);
      if (sections.length) body.set('sections', sections.join(','));
      body.set('sections_url', window.location.pathname);
      const response = await fetch(root() + 'cart/add.js', { method: 'POST', body, headers: { Accept: 'application/json' } });
      const result = await response.json();
      if (!response.ok) throw new Error(typeof result.description === 'string' ? result.description : status?.dataset.ocError);
      confirmed = true;
      if (status) status.textContent = status.dataset.ocAdded;
      const drawer = document.querySelector('cart-drawer-component');
      if (!drawer || typeof drawer.open !== 'function' || !sections.length || sections.some((id) => !result.sections?.[id])) {
        window.location.assign(root() + 'cart');
        return;
      }
      const cartResponse = await fetch(root() + 'cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!cartResponse.ok) throw new Error();
      const cart = await cartResponse.json();
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: { resource: cart, sourceId: 'oc-add-to-cart', data: { source: 'oc-add-to-cart', itemCount: cart.item_count, sections: result.sections } }
      }));
      requestAnimationFrame(() => document.querySelector('cart-drawer-component')?.open());
    } catch (error) {
      if (confirmed) {
        window.location.assign(root() + 'cart');
        return;
      }
      if (status) {
        status.setAttribute('data-error', '');
        status.textContent = error instanceof TypeError ? status.dataset.ocError : error.message || status.dataset.ocError;
      }
    } finally {
      adding = false;
      form.removeAttribute('aria-busy');
      if (label) label.textContent = original;
      buttons.forEach((item) => { item.disabled = item.dataset.ocAvailable !== 'true'; });
    }
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => setup());
  else setup();
  document.addEventListener('shopify:section:load', (event) => setup(event.target));
  window.addEventListener('pageshow', async (event) => {
    if (!event.persisted) return;
    try {
      const response = await fetch(root() + 'cart.js', { cache: 'no-store' });
      if (!response.ok) return;
      const cart = await response.json();
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: { resource: cart, sourceId: 'oc-history', data: { source: 'oc-history', itemCount: cart.item_count, sections: {} } }
      }));
    } catch (_) {}
  });
})();
